import {
  type Permission,
  type PermissionEffect,
  PERMISSION_MODULES,
  PERMISSIONS,
  dependentsOf,
  resolveEffective,
  withRequirements,
} from '#shared/types';

/** One line of the matrix: a thing, and the actions on it. */
interface MatrixRow {
  label: string;
  keys: Permission[];
}

export interface MatrixModule {
  key: string;
  label: string;
  description: string;
  rows: MatrixRow[];
}

/** The catalog, shaped for drawing: modules, then rows, then action keys. */
export const MATRIX: readonly MatrixModule[] = PERMISSION_MODULES.map((module) => ({
  key: module.key,
  label: module.label,
  description: module.description,
  rows: module.items.map((item) => ({
    label: item.label,
    keys: Object.keys(item.permissions) as Permission[],
  })),
}));

export const MATRIX_KEYS: readonly Permission[] = MATRIX.flatMap((permissionModule) =>
  permissionModule.rows.flatMap((row) => row.keys),
);

type GrantState = 'all' | 'some' | 'none';

/**
 * Ticks or unticks `keys`, keeping the set coherent.
 *
 * Ticking brings along what each key requires — "Edit" without "View" is a
 * button on a screen nobody can open. Unticking takes away what depends on it,
 * for the same reason in reverse.
 */
export function setGranted(
  selected: ReadonlySet<Permission>,
  keys: readonly Permission[],
  shouldGrant: boolean,
): Set<Permission> {
  const next = new Set(selected);
  if (shouldGrant) {
    for (const key of withRequirements(keys)) next.add(key);
  } else {
    for (const key of keys) {
      next.delete(key);
      for (const dependent of dependentsOf(key)) next.delete(dependent);
    }
  }
  return next;
}

export function grantState(
  selected: ReadonlySet<Permission>,
  keys: readonly Permission[],
): GrantState {
  const held = keys.filter((key) => selected.has(key)).length;
  if (held === 0) return 'none';
  return held === keys.length ? 'all' : 'some';
}

/** What else ticking `key` will tick, for the tooltip that says so. */
function requirementsOf(key: Permission): Permission[] {
  return [...withRequirements([key])].filter((other) => other !== key);
}

/**
 * The matrix narrowed to what matches `query` — module, item, action,
 * description or the raw key. A module whose name matches keeps every row.
 */
export function filterMatrix(query: string): MatrixModule[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...MATRIX];
  const hit = (text: string) => text.toLowerCase().includes(needle);

  return MATRIX.flatMap((module) => {
    if (hit(module.label)) return [module];
    const rows = module.rows.flatMap((row) => {
      if (hit(row.label)) return [row];
      const keys = row.keys.filter(
        (key) => hit(key) || hit(PERMISSIONS[key].action) || hit(PERMISSIONS[key].description),
      );
      return keys.length > 0 ? [{ ...row, keys }] : [];
    });
    return rows.length > 0 ? [{ ...module, rows }] : [];
  });
}

/* ---- per-user exceptions ------------------------------------------------ */

export type OverrideState = 'inherit' | PermissionEffect;

export type Overrides = ReadonlyMap<Permission, PermissionEffect>;

/**
 * The next state on a click. The first click always changes the outcome — deny
 * what the role grants, allow what it does not — and the second pins the
 * opposite, which holds even if the role is changed later.
 */
export function nextOverride(state: OverrideState, fromRole: boolean): OverrideState {
  const cycle: readonly OverrideState[] = fromRole
    ? ['inherit', 'deny', 'allow']
    : ['inherit', 'allow', 'deny'];
  return cycle[(cycle.indexOf(state) + 1) % cycle.length]!;
}

export function setOverride(
  overrides: Overrides,
  key: Permission,
  state: OverrideState,
): Map<Permission, PermissionEffect> {
  const next = new Map(overrides);
  if (state === 'inherit') next.delete(key);
  else next.set(key, state);
  return next;
}

/** What the person would end up with — the server computes the same thing. */
export function effectiveWith(
  rolePermissions: readonly Permission[],
  overrides: Overrides,
): Set<Permission> {
  return resolveEffective({
    rolePermissions,
    overrides: [...overrides].map(([permission, effect]) => ({ permission, effect })),
    isSuper: false,
  });
}

/** The list the API takes, in catalog order so a diff of two saves is readable. */
export function overridesPayload(
  overrides: Overrides,
): { permission: Permission; effect: PermissionEffect }[] {
  return MATRIX_KEYS.filter((key) => overrides.has(key)).map((permission) => ({
    permission,
    effect: overrides.get(permission)!,
  }));
}

export const STATE_LABEL: Record<OverrideState, string> = {
  inherit: 'from role',
  allow: 'allowed',
  deny: 'denied',
};

/** A grant checkbox's tooltip: what the permission is, and what it brings with it. */
export function grantTooltip(permission: Permission): string {
  const requires = requirementsOf(permission);
  const brings =
    requires.length > 0
      ? `\nAlso grants: ${requires.map((requirement) => PERMISSIONS[requirement].action).join(', ')}`
      : '';
  return PERMISSIONS[permission].description + brings;
}

/** An override cell's tooltip: the outcome, and where it comes from. */
export function overrideTooltip(
  permission: Permission,
  {
    state,
    isGranted,
    isFromRole,
  }: { state: OverrideState; isGranted: boolean; isFromRole: boolean },
): string {
  const source =
    state === 'inherit' ? (isFromRole ? ' (role grants it)' : ' (role does not grant it)') : '';
  return `${PERMISSIONS[permission].description}\n${isGranted ? 'Granted' : 'Not granted'} — ${STATE_LABEL[state]}${source}`;
}
